const BOLD_UPPER_START = 0x1d400;
const BOLD_LOWER_START = 0x1d41a;
const BOLD_DIGIT_START = 0x1d7ce;
const ITALIC_UPPER_START = 0x1d434;
const ITALIC_LOWER_START = 0x1d44e;
const BOLD_ITALIC_UPPER_START = 0x1d468;
const BOLD_ITALIC_LOWER_START = 0x1d482;

const UNDERLINE_MARK = "\u0332";
const STRIKE_MARK = "\u0336";

const getStyledCharacter = (character, styleState) => {
  const { bold, italic } = styleState;
  const code = character.codePointAt(0);

  if (bold && italic) {
    if (character >= "A" && character <= "Z") {
      return String.fromCodePoint(
        BOLD_ITALIC_UPPER_START + (code - "A".codePointAt(0))
      );
    }

    if (character >= "a" && character <= "z") {
      return String.fromCodePoint(
        BOLD_ITALIC_LOWER_START + (code - "a".codePointAt(0))
      );
    }
  }

  if (bold) {
    if (character >= "A" && character <= "Z") {
      return String.fromCodePoint(
        BOLD_UPPER_START + (code - "A".codePointAt(0))
      );
    }

    if (character >= "a" && character <= "z") {
      return String.fromCodePoint(
        BOLD_LOWER_START + (code - "a".codePointAt(0))
      );
    }

    if (character >= "0" && character <= "9") {
      return String.fromCodePoint(
        BOLD_DIGIT_START + (code - "0".codePointAt(0))
      );
    }
  }

  if (italic) {
    if (character >= "A" && character <= "Z") {
      return String.fromCodePoint(
        ITALIC_UPPER_START + (code - "A".codePointAt(0))
      );
    }

    if (character >= "a" && character <= "z") {
      if (character === "h") return "\u210e";

      return String.fromCodePoint(
        ITALIC_LOWER_START + (code - "a".codePointAt(0))
      );
    }
  }

  return character;
};

const applyDecorations = (value, styleState) => {
  if (!value) return "";

  return Array.from(value)
    .map((character) => {
      if (character === "\n") return "\n";

      const styledCharacter = getStyledCharacter(character, styleState);

      if (/\s/.test(character)) {
        return styledCharacter;
      }

      let decoratedCharacter = styledCharacter;

      if (styleState.underline) {
        decoratedCharacter += UNDERLINE_MARK;
      }

      if (styleState.strike) {
        decoratedCharacter += STRIKE_MARK;
      }

      return decoratedCharacter;
    })
    .join("");
};

const normalizeTextNode = (value = "") => {
  // A text node that is ENTIRELY whitespace and contains a newline is just
  // HTML pretty-printing between block-level tags — draftToHtml's own
  // serializer puts a literal "\n" between every "<p>...</p>", which
  // DOMParser sees as its own whitespace-only text-node sibling, not real
  // pasted/typed content. Counting that node's "\n" added one phantom
  // blank line between EVERY paragraph pair on top of whatever the <p>
  // handling below already contributes, which is exactly why tightly
  // adjacent lines (no real blank line between them) still rendered with
  // a visible gap, and a real single blank line rendered as two.
  if (/^\s*$/.test(value) && /\n/.test(value)) return "";
  // Only collapses tab/form-feed/carriage-return — those are never
  // meaningful in visible text, just HTML-source noise. A run of literal
  // space characters (e.g. Draft.js encoding "hello    world" as alternating
  // spaces/&nbsp; to survive HTML's own whitespace collapsing) is real,
  // intentional content the user typed and must survive untouched — this
  // used to also collapse runs of plain spaces down to one, silently
  // undoing multiple-space spacing on every save/preview.
  return value.replace(/[\t\f\r]+/g, " ");
};

export const convertHtmlToLinkedInStyledText = (html = "") => {
  if (!html) return "";

  const parser = new DOMParser();
  const document = parser.parseFromString(html, "text/html");

  const traverse = (
    node,
    styleState = {
      bold: false,
      italic: false,
      underline: false,
      strike: false,
    }
  ) => {
    if (!node) return "";

    if (node.nodeType === Node.TEXT_NODE) {
      return applyDecorations(normalizeTextNode(node.nodeValue || ""), styleState);
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    const tagName = node.nodeName.toUpperCase();

    if (tagName === "BR") {
      return "\n";
    }

    const nextStyleState = { ...styleState };

    if (tagName === "B" || tagName === "STRONG") nextStyleState.bold = true;
    if (tagName === "I" || tagName === "EM") nextStyleState.italic = true;
    if (tagName === "U") nextStyleState.underline = true;
    if (tagName === "S" || tagName === "STRIKE" || tagName === "DEL") {
      nextStyleState.strike = true;
    }

    const children = Array.from(node.childNodes || [])
      .map((child) => traverse(child, nextStyleState))
      .join("");

    if (tagName === "LI") {
      const parentTag = String(node.parentNode?.nodeName || "").toUpperCase();
      const siblings = Array.from(node.parentNode?.children || []);
      const index = siblings.indexOf(node);
      const prefix = parentTag === "OL" ? `${index + 1}. ` : "- ";
      const value = children.trim();
      return value ? `${prefix}${value}\n` : "";
    }

    if (tagName === "UL" || tagName === "OL") {
      return `${children}\n`;
    }

    if (tagName === "A") {
      const href = node.getAttribute("href");
      const value = children.trim();

      if (!href || !value) return value;
      if (value === href) return href;
      return `${value} (${href})`;
    }

    if (
      tagName === "P" ||
      tagName === "DIV" ||
      tagName === "SECTION" ||
      tagName === "ARTICLE" ||
      tagName === "BLOCKQUOTE"
    ) {
      const value = children.trim();
      // Every paragraph — empty or not — ends with exactly one "\n". A real
      // paragraph contributes its text plus that one boundary "\n"; an empty
      // paragraph (just a <br>, whitespace, or nothing) is Draft.js's
      // representation of the user pressing Enter for a deliberate blank
      // line, and contributes only that "\n". This way N consecutive empty
      // paragraphs between two paragraphs of text produces exactly N blank
      // lines: 0 empty paragraphs joins them tight (a single "\n", no gap —
      // ordinary paragraph breaks shouldn't silently invent spacing the user
      // never typed), 1 produces one visible blank line, and so on. The
      // previous version had real paragraphs contribute "\n\n" — a *baseline*
      // blank line between every paragraph pair regardless of whether the
      // user added one — so a single deliberately-typed blank line stacked on
      // top of that invisible baseline and rendered as two, exactly the "one
      // line space looks like two" bug.
      return value ? `${value}\n` : "\n";
    }

    return children;
  };

  const plainText = traverse(document.body)
    // No collapsing here — each paragraph the loop above walked through
    // already contributed exactly one "\n" of its own, so however many blank
    // lines the user actually typed (one, two, however many Enters) comes
    // through faithfully. A previous version of this capped every run down to
    // a single blank line regardless of how many were typed, which quietly
    // undid a second (or third...) intentional blank line on every save —
    // exactly the "I pressed Enter twice but only one line survives" bug.
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .trim();

  // LinkedIn (and every other consumer of this plain string) has no HTML/dir
  // attribute to lean on, so it resolves bidi direction per its own rendering
  // context. Wrapping each paragraph in First Strong Isolate / Pop Directional
  // Isolate marks bakes "auto-detect direction, don't bleed into neighbours"
  // directly into the text itself, so mixed Arabic/Latin/emoji paragraphs
  // render in a consistent, correct order wherever the string ends up
  // (LinkedIn feed, notifications, copy-paste) — not just in our own preview.
  const FSI = "⁨";
  const PDI = "⁩";

  // Paragraphs are now separated by a run of one-or-more "\n" (one for a
  // tight break, more for user-added blank lines) rather than always exactly
  // "\n\n", so split on runs of newlines instead of a fixed "\n\n" — with the
  // newline runs captured (the parens) so they pass straight through
  // unchanged and every paragraph's original spacing is preserved exactly.
  return plainText
    .split(/(\n+)/)
    .map((chunk) => (chunk && !/^\n+$/.test(chunk) ? `${FSI}${chunk}${PDI}` : chunk))
    .join("");
};
